import React from 'react';
import { IconAlertCircle, IconAlertTriangle } from '@tabler/icons-react';
import { motion } from 'framer-motion';

const ErrorCards = ({ fatalErrors, nonFatalErrors,filters }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.96 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: i * 0.2,
        duration: 0.7,
        type: 'spring',
        stiffness: 60,
      },
    }),
  };

  const iconVariants = {
    hidden: { opacity: 0, scale: 0.6, rotate: -15 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: { delay: 0.4, type: 'spring', stiffness: 120 },
    },
  };

  const Card = ({ title, icon, data, type, index, color, gradientFrom, gradientTo }) => (
    <motion.div
      custom={index}
      initial="hidden"
      animate="visible"
      variants={cardVariants}
      className="relative overflow-hidden rounded-[2rem] p-6 bg-white/50 backdrop-blur-xl border border-white/20 shadow-[0_12px_25px_rgba(0,0,0,0.1)] hover:shadow-2xl transition-all duration-300 hover:scale-[1.03]"
    >
      {/* Decorative Abstract Blobs */}
      <div
        className="absolute -top-12 -left-12 w-36 h-36 rounded-full blur-2xl opacity-20 z-0"
        style={{ background: gradientFrom }}
      />
      <div
        className="absolute -bottom-12 -right-12 w-28 h-28 rounded-full blur-2xl opacity-30 z-0"
        style={{ background: gradientTo }}
      />

      {/* Card Header */}
      <div className="relative z-10 flex items-center space-x-4 mb-4">
        <motion.div
          className="p-3 rounded-full shadow-lg"
          style={{ backgroundColor: gradientFrom, color: '#fff' }}
          variants={iconVariants}
          initial="hidden"
          animate="visible"
        >
          {icon}
        </motion.div>
        <h5 className="text-xl font-bold" style={{ color }}>{title}</h5>
      </div>

      {/* Error List */}
      <div className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scroll z-10">
        {data.length > 0 ? (
          data.map((e, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + i * 0.1 }}
              className="p-3 rounded-lg text-sm font-medium"
              style={{
                backgroundColor: `${gradientFrom}15`,
                color: '#1A2B5C',
              }}
            >
              <span>{e['Error Sub Category']}</span>: <strong>{e[`total${type}ErrorCount`]}</strong>
            </motion.div>
          ))
        ) : (
          <motion.div
            className="flex items-center gap-3 p-4 rounded-xl shadow-md bg-gradient-to-r from-gray-800 to-gray-700 text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <IconAlertCircle size={22} className="text-red-400" />
            <span className="text-sm font-semibold">No {type.toLowerCase()} errors found</span>
          </motion.div>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 px-4 mb-12">
      <Card
        title="Fatal Errors"
        icon={<IconAlertCircle size={28} />}
        data={fatalErrors}
        type="Fatal"
        index={0}
        color="#EE1D23"
        gradientFrom="#EE1D23"
        gradientTo="#f87171"
      />
      <Card
        title="Non-Fatal Errors"
        icon={<IconAlertTriangle size={28} />}
        data={nonFatalErrors}
        type="NonFatal"
        index={1}
        color="#1A2B5C"
        gradientFrom="#1A2B5C"
        gradientTo="#60a5fa"
      />
    </div>
  );
};

export default ErrorCards;
